/* eslint-disable no-unused-vars */
const SUPPORTED_COMPONENTS = [
  'card', 'chart', 'drawer', 'task-progress',
  'sheet', 'timeline-log', 'result-summary', 'pagelist',
  'iframe', 'postmessage', 'recommendation', 'recommended-question',
  'center-iframe', 'core-dashboard', 'analysis-summary', 'key-judgment',
  'decision-suggestion', 'analysis-conclusion', 'key-finding',
  'strengths-weaknesses', 'metric-compare', 'metric-analysis',
  'section-title', 'section-block', 'chat-card-create-task'
]

const t = (key, params) => window.$i18n ? window.$i18n.t(key, params) : key

const COMPONENT_TYPE_KEYS = {
  card: 'workbench.compCard',
  drawer: 'workbench.compDrawer',
  chart: 'workbench.compChart',
  'task-progress': 'workbench.compTaskProgress',
  sheet: 'workbench.compSheet',
  'timeline-log': 'workbench.compTimeline',
  'result-summary': 'workbench.compResultSummary',
  pagelist: 'workbench.compPagelist',
  iframe: 'workbench.compIframe',
  'center-iframe': 'workbench.compIframe',
  'editable_form': 'workbench.compEditableForm',
  recommendation: 'workbench.compRecommendation',
  'recommended-question': 'workbench.compRecommendation',
  'core-dashboard': 'workbench.compCoreDashboard',
  'analysis-summary': 'workbench.compAnalysisSummary',
  'key-judgment': 'workbench.compKeyJudgment',
  'decision-suggestion': 'workbench.compDecisionSuggestion',
  'analysis-conclusion': 'workbench.compAnalysisConclusion',
  'key-finding': 'workbench.compKeyFinding',
  'strengths-weaknesses': 'workbench.compStrengthsWeaknesses',
  'metric-compare': 'workbench.compMetricCompare',
  'metric-analysis': 'workbench.compMetricAnalysis',
  'section-title': 'workbench.compSectionTitle',
  'chat-card-create-task': 'workbench.compChatCardCreateTask'
}

export const COMPONENT_TYPE_NAMES = new Proxy(COMPONENT_TYPE_KEYS, {
  get(target, prop) {
    if (prop in target) return t(target[prop])
    return undefined
  }
})

const decodeJsonContent = (jsonContent) => {
  return jsonContent
    .trim()
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
}

const normalizeLooseJson = (value) => {
  let normalized = value.trim()
  normalized = normalized.replace(/,\s*([}\]])/g, '$1')
  normalized = normalized.replace(
    /([{,]\s*)([A-Za-z_][\w-]*)(\s*:)/g,
    '$1"$2"$3'
  )
  normalized = normalized.replace(
    /'([^'\\]*(?:\\.[^'\\]*)*)'/g,
    (_m, inner) => {
      const escaped = String(inner).replace(/"/g, '\\"')
      return `"${escaped}"`
    }
  )
  return normalized
}

// 修复 LLM 输出的 JSON 中字符串值内含未正确转义的引号
// 通过括号匹配找到字符串值的真正边界，再修复内部引号
const repairJson = (json) => {
  // 找到 "key": "value" 模式，其中 value 以 [ 或 { 开头
  // 需要通过括号平衡找到 value 的真正结束位置
  const keyPattern = /"(\w+)"\s*:\s*"/g
  let match
  const fixes = []

  while ((match = keyPattern.exec(json)) !== null) {
    const key = match[1]
    const valOpenQuote = match.index + match[0].length - 1 // 位置在 value 的 "

    // 检查 value 的第一个字符是否为 [ 或 {
    const firstChar = json[valOpenQuote + 1]
    if (firstChar !== '[' && firstChar !== '{') continue

    // 通过括号平衡找到字符串值的真正结束引号
    const openChar = firstChar
    const closeChar = openChar === '[' ? ']' : '}'
    let depth = 0
    let inStr = false
    let esc = false
    let valEndQuote = -1
    let j = valOpenQuote + 1

    while (j < json.length) {
      const ch = json[j]
      if (inStr) {
        if (esc) { esc = false; j++; continue }
        if (ch === '\\') { esc = true; j++; continue }
        if (ch === '"') { inStr = false; j++; continue }
        j++
        continue
      }
      // 不在字符串内
      if (ch === '"') { inStr = true; j++; continue }
      if (ch === openChar) { depth++; j++; continue }
      if (ch === closeChar) {
        depth--
        if (depth === 0) {
          // 找到匹配的闭合括号，下一个 " 就是 value 的结束引号
          let k = j + 1
          while (k < json.length && (json[k] === ' ' || json[k] === '\n' || json[k] === '\r' || json[k] === '\t')) k++
          if (k < json.length && json[k] === '"') {
            valEndQuote = k
          }
          break
        }
        j++
        continue
      }
      j++
    }

    if (valEndQuote === -1) continue

    // 提取 value 的原始内容
    const rawValue = json.slice(valOpenQuote + 1, valEndQuote)

    // 尝试直接解析
    try {
      JSON.parse(`"${rawValue}"`)
      continue // 已经合法，不需要修复
    } catch { /* 需要修复 */ }

    // 修复策略：去掉所有反斜杠转义，再重新正确转义
    // 处理 \" → " 和 \\ → \
    let unescaped = rawValue.replace(/\\"/g, '"')
    unescaped = unescaped.replace(/\\\\/g, '\\')
    // 重新转义: \ → \\, " → \"
    const reEscaped = unescaped.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    fixes.push({
      start: valOpenQuote,
      end: valEndQuote + 1,
      replacement: `"${reEscaped}"`
    })
  }

  if (fixes.length === 0) return json

  // 从后往前替换，避免索引偏移
  let result = json
  for (let i = fixes.length - 1; i >= 0; i--) {
    const f = fixes[i]
    result = result.slice(0, f.start) + f.replacement + result.slice(f.end)
  }
  return result
}

const safeParseJson = (jsonContent) => {
  // 预处理：提取 function(...){...} 替换为占位符，JSON.parse 后再恢复
  const funcs = []
  let prepared = jsonContent
  let searchIdx = 0
  while (searchIdx < prepared.length) {
    const fnMatch = prepared.slice(searchIdx).match(/:\s*function\s*\(/)
    if (!fnMatch) break
    const start = searchIdx + fnMatch.index
    const bodyStart = prepared.indexOf('{', start + fnMatch[0].length)
    if (bodyStart === -1) break
    let depth = 1
    let bodyEnd = -1
    for (let i = bodyStart + 1; i < prepared.length; i++) {
      if (prepared[i] === '{') depth++
      else if (prepared[i] === '}') {
        depth--
        if (depth === 0) { bodyEnd = i; break }
      }
    }
    if (bodyEnd === -1) break
    const colonIdx = prepared.indexOf(':', start)
    const idx = funcs.length
    funcs.push(prepared.slice(colonIdx + 1, bodyEnd + 1).trim())
    prepared = prepared.slice(0, colonIdx + 1) + ` "__ECHARTS_FUNC_${idx}__"` + prepared.slice(bodyEnd + 1)
    searchIdx = colonIdx + 1
  }
  try {
    const parsed = JSON.parse(prepared)
    if (funcs.length > 0) restoreEchartsFunctions(parsed, funcs)
    // 兜底：解析后仍有 function 字符串的转为真实 Function
    convertFunctionStrings(parsed)
    return parsed
  } catch {
    try {
      return JSON.parse(normalizeLooseJson(prepared))
    } catch {
      try {
        const repaired = repairJson(prepared)
        if (repaired !== prepared) {
          const p = JSON.parse(repaired)
          if (funcs.length > 0) restoreEchartsFunctions(p, funcs)
          return p
        }
      } catch { /* ignore */ }
      return null
    }
  }
}

// 兜底：将解析后对象中形如 "function(...){...}" 的字符串转为 Function
function convertFunctionStrings(obj) {
  if (!obj || typeof obj !== 'object') return
  for (const key of Object.keys(obj)) {
    const val = obj[key]
    if (typeof val === 'string' && /^\s*function\s*\(/.test(val)) {
      try { obj[key] = new Function('return ' + val)() } catch { /* ignore */ }
    } else if (typeof val === 'object') {
      convertFunctionStrings(val)
    }
  }
}

function restoreEchartsFunctions(obj, funcs) {
  if (!obj || typeof obj !== 'object') return
  for (const key of Object.keys(obj)) {
    const val = obj[key]
    if (typeof val === 'string' && val.startsWith('__ECHARTS_FUNC_')) {
      const idx = parseInt(val.replace('__ECHARTS_FUNC_', '').replace('__', ''), 10)
      if (!isNaN(idx) && funcs[idx]) {
        try { obj[key] = new Function('return ' + funcs[idx])() } catch { /* ignore */ }
      }
    } else if (typeof val === 'object') {
      restoreEchartsFunctions(val, funcs)
    }
  }
}

// 宽松解析：值可能是对象，也可能是 JSON 字符串
const parseMaybeJson = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'object') return value
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return safeParseJson(decodeJsonContent(trimmed))
}

const hasSeries = (option) => {
  if (!option || typeof option !== 'object') return false
  return Array.isArray(option.series) && option.series.length > 0
}

// 组件 props 结构契约：返回 null 表示结构可用，返回字符串表示不可用的原因
const COMPONENT_CONTRACTS = {
  chart(props) {
    const option = parseMaybeJson(props.option)
    if (hasSeries(option)) return null
    // 兼容嵌套 { option: { option: {...} } } 的写法
    if (option && hasSeries(parseMaybeJson(option.option))) return null
    const data = parseMaybeJson(props.data)
    if (Array.isArray(data) && data.length > 0) return null
    if (props.api && props.showConfig) return null
    return 'chart.missingSeries'
  }
}

const validateComponentProps = (tagName, props) => {
  const contract = COMPONENT_CONTRACTS[tagName]
  if (!contract) return null
  try {
    return contract(props || {})
  } catch {
    return 'contract.error'
  }
}

// 从解析后的 JSON 中提取组件名（不依赖白名单）
const resolveComponentTagName = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return null
  if (typeof parsed.component === 'string') return parsed.component.toLowerCase()
  if (typeof parsed.type === 'string') return parsed.type.toLowerCase()
  const keys = Object.keys(parsed)
  if (keys.length === 1) return keys[0].toLowerCase()
  return null
}

// 从解析后的 JSON 中提取原始 props（不依赖白名单）
const resolveRawProps = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return {}
  if (parsed.props && typeof parsed.props === 'object') return parsed.props
  if (typeof parsed.component === 'string') {
    const { component, ...rest } = parsed
    return rest
  }
  if (typeof parsed.type === 'string') {
    const { type, ...rest } = parsed
    return rest
  }
  const keys = Object.keys(parsed)
  if (keys.length === 1) {
    const rawValue = parsed[keys[0]]
    if (rawValue && typeof rawValue === 'object') {
      return rawValue.props && typeof rawValue.props === 'object' ? rawValue.props : rawValue
    }
  }
  return {}
}

/**
 * 诊断一段卡片 JSON 的失败原因，供兜底提示使用
 * code: json.syntax（JSON 非法）| json.structure（结构不符合定义）| component.unsupported（未知组件）
 * @returns {{ code: string, componentName: string|null } | null} 可正常解析时返回 null
 */
export function diagnoseComponentJson(jsonContent) {
  if (!jsonContent || typeof jsonContent !== 'string') {
    return { code: 'json.syntax', componentName: null }
  }
  const parsed = safeParseJson(decodeJsonContent(jsonContent))
  if (!parsed || typeof parsed !== 'object') {
    return { code: 'json.syntax', componentName: null }
  }
  const tagName = resolveComponentTagName(parsed)
  if (!tagName) return { code: 'json.structure', componentName: null }
  if (!SUPPORTED_COMPONENTS.includes(tagName)) {
    return { code: 'component.unsupported', componentName: tagName }
  }
  if (validateComponentProps(tagName, resolveRawProps(parsed))) {
    return { code: 'json.structure', componentName: tagName }
  }
  return null
}

const parseComponentPayload = (jsonContent) => {
  try {
    const processedJson = decodeJsonContent(jsonContent)
    const parsed = safeParseJson(processedJson)

    if (!parsed || typeof parsed !== 'object') return null

    if (typeof parsed.component === 'string') {
      const tagName = parsed.component.toLowerCase()
      if (!SUPPORTED_COMPONENTS.includes(tagName)) return null

      let props = {}
      if (parsed.props && typeof parsed.props === 'object') {
        props = { ...parsed.props }
      } else {
        const { component, ...rest } = parsed
        props = rest
      }

      if (tagName === 'task-progress' && !props.type) {
        props.type = 'task-progress'
      }

      if (tagName === 'card' && props.type === 'pageList') {
        props._rawJson = JSON.stringify(props)
        for (const key of Object.keys(props)) {
          if (key === '_rawJson' || key === '_componentKey') continue
          const val = props[key]
          if (val !== null && val !== undefined && typeof val === 'object') {
            props[key] = JSON.stringify(val)
          }
        }
        return { tagName: 'pagelist', props }
      }

      // drawer 组件：fields → formConfig 统一映射
      if (tagName === 'drawer') {
        if (!props.formConfig && props.fields) {
          props.formConfig = props.fields
        }
        if (!props.formConfig && props._rawJson) {
          try {
            const raw = JSON.parse(props._rawJson)
            const fc = raw.formConfig || raw.fields
            if (fc) props.formConfig = typeof fc === 'string' ? fc : JSON.stringify(fc)
          } catch (e) { /* ignore */ }
        }
      }

      // recommended-question → recommendation 组件复用
      if (tagName === 'recommended-question') {
        return { tagName: 'recommendation', props }
      }

      props._rawJson = JSON.stringify(props)

      for (const key of Object.keys(props)) {
        if (key === '_rawJson' || key === '_componentKey') continue
        const val = props[key]
        if (val !== null && val !== undefined && typeof val === 'object') {
          props[key] = JSON.stringify(val)
        }
      }

      if (validateComponentProps(tagName, props)) return null

      return { tagName, props }
    }

    if (typeof parsed.type === 'string') {
      const tagName = parsed.type.toLowerCase()
      if (SUPPORTED_COMPONENTS.includes(tagName)) {
        let props = {}
        if (parsed.props && typeof parsed.props === 'object') {
          props = { ...parsed.props }
        } else {
          const { type, ...rest } = parsed
          props = rest
        }

        if (tagName === 'task-progress' && !props.type) {
          props.type = 'task-progress'
        }

        if (tagName === 'card' && props.type === 'pageList') {
          props._rawJson = JSON.stringify(props)
          for (const key of Object.keys(props)) {
            if (key === '_rawJson' || key === '_componentKey') continue
            const val = props[key]
            if (val !== null && val !== undefined && typeof val === 'object') {
              props[key] = JSON.stringify(val)
            }
          }
          return { tagName: 'pagelist', props }
        }

        if (tagName === 'drawer') {
          if (!props.formConfig && props.fields) {
            props.formConfig = props.fields
          }
          if (!props.formConfig && props._rawJson) {
            try {
              const raw = JSON.parse(props._rawJson)
              const fc = raw.formConfig || raw.fields
              if (fc) props.formConfig = typeof fc === 'string' ? fc : JSON.stringify(fc)
            } catch (e) { /* ignore */ }
          }
        }

        if (tagName === 'recommended-question') {
          return { tagName: 'recommendation', props }
        }

        props._rawJson = JSON.stringify(props)

        for (const key of Object.keys(props)) {
          if (key === '_rawJson' || key === '_componentKey') continue
          const val = props[key]
          if (val !== null && val !== undefined && typeof val === 'object') {
            props[key] = JSON.stringify(val)
          }
        }

        if (validateComponentProps(tagName, props)) return null

        return { tagName, props }
      }
    }

    // 兼容格式: {"chart": {...props}} 组件类型作为顶层 key
    const parsedKeys = Object.keys(parsed)
    if (parsedKeys.length === 1) {
      const topKey = parsedKeys[0].toLowerCase()
      if (SUPPORTED_COMPONENTS.includes(topKey)) {
        const rawValue = parsed[parsedKeys[0]]
        let props = {}
        if (rawValue && typeof rawValue === 'object') {
          if (rawValue.props && typeof rawValue.props === 'object') {
            props = { ...rawValue.props }
          } else {
            props = { ...rawValue }
          }
        }

        if (topKey === 'task-progress' && !props.type) {
          props.type = 'task-progress'
        }

        if (topKey === 'card' && props.type === 'pageList') {
          props._rawJson = JSON.stringify(props)
          for (const key of Object.keys(props)) {
            if (key === '_rawJson' || key === '_componentKey') continue
            const val = props[key]
            if (val !== null && val !== undefined && typeof val === 'object') {
              props[key] = JSON.stringify(val)
            }
          }
          return { tagName: 'pagelist', props }
        }

        if (topKey === 'drawer') {
          if (!props.formConfig && props.fields) {
            props.formConfig = props.fields
          }
          if (!props.formConfig && props._rawJson) {
            try {
              const raw = JSON.parse(props._rawJson)
              const fc = raw.formConfig || raw.fields
              if (fc) props.formConfig = typeof fc === 'string' ? fc : JSON.stringify(fc)
            } catch (e) { /* ignore */ }
          }
        }

        if (topKey === 'recommended-question') {
          return { tagName: 'recommendation', props }
        }

        props._rawJson = JSON.stringify(props)

        for (const key of Object.keys(props)) {
          if (key === '_rawJson' || key === '_componentKey') continue
          const val = props[key]
          if (val !== null && val !== undefined && typeof val === 'object') {
            props[key] = JSON.stringify(val)
          }
        }

        if (validateComponentProps(topKey, props)) return null

        return { tagName: topKey, props }
      }
    }

    return null
  } catch {
    return null
  }
}

export function findBalancedJsonObject(value, startIndex) {
  let depth = 0
  let inString = false
  let escaped = false

  for (let i = startIndex; i < value.length; i++) {
    const ch = value[i]

    if (inString) {
      if (escaped) { escaped = false; continue }
      if (ch === '\\') { escaped = true; continue }
      if (ch === '"') { inString = false }
      continue
    }

    if (ch === '"') { inString = true; continue }
    if (ch === '{') { depth += 1; continue }
    if (ch === '}') {
      depth -= 1
      if (depth === 0) return i
    }
  }
  return -1
}

/**
 * 括号配对失败（JSON 被截断/缺右括号）时，按空行边界估算卡片结束位置
 * 卡片通常独占一段（前后各一个空行），空行之后的正文应保留给 markdown 渲染
 * @param {string} value 原始文本
 * @param {number} startIndex 卡片 JSON 起点（{）
 * @returns {number} 卡片结束位置（不含），找不到边界返回 -1
 */
export function findTruncatedJsonObjectEnd(value, startIndex) {
  // 兼容真实换行与字面量 \n（部分模型输出会被二次转义）
  const blankLineRegex = /\n[ \t\r]*\n|\\n[ \t]*\\n/g
  blankLineRegex.lastIndex = startIndex
  const match = blankLineRegex.exec(value)
  if (!match) return -1
  const end = value.slice(startIndex, match.index).replace(/[\s]+$/, '').length + startIndex
  return end > startIndex ? end : -1
}

function findInlineJsonComponentMatches(value) {
  const matches = []
  const markerRegex = /(?:"component"|'component'|\bcomponent\b)\s*:/gi
  let searchIndex = 0

  while (searchIndex < value.length) {
    markerRegex.lastIndex = searchIndex
    const markerMatch = markerRegex.exec(value)
    if (!markerMatch || markerMatch.index === undefined) break

    const markerIndex = markerMatch.index
    const markerLength = markerMatch[0].length

    const objectStart = value.lastIndexOf('{', markerIndex)
    if (objectStart === -1 || objectStart >= markerIndex) {
      searchIndex = markerIndex + markerLength
      continue
    }

    const objectEnd = findBalancedJsonObject(value, objectStart)
    if (objectEnd === -1) {
      searchIndex = markerIndex + markerLength
      continue
    }

    const jsonContent = value.slice(objectStart, objectEnd + 1)
    const payload = parseComponentPayload(jsonContent)
    if (payload) {
      matches.push({ start: objectStart, end: objectEnd + 1, jsonContent, payload })
    }
    searchIndex = objectEnd + 1
  }

  return matches
}

const JSON_FENCED_BLOCK_REGEX = /```(?:json\b)?\s*\n?([\s\S]*?)\n?\s*```/gi

function decodeHtmlEntities(str) {
  return str
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
}

function findFencedCodeBlockMatches(value) {
  const matches = []

  JSON_FENCED_BLOCK_REGEX.lastIndex = 0
  let match = JSON_FENCED_BLOCK_REGEX.exec(value)
  while (match) {
    const blockStart = match.index
    const blockEnd = match.index + match[0].length
    const jsonContent = match[1] || ''
    const payload = parseComponentPayload(jsonContent)
    if (payload) {
      matches.push({ start: blockStart, end: blockEnd, jsonContent, payload })
    }
    match = JSON_FENCED_BLOCK_REGEX.exec(value)
  }

  return matches
}

/**
 * 判断一段 JSON 文本是否为“卡片形态”（trim 后以 { 开头且 component/type 取值为受支持的组件名）。
 * 供校验修复器使用：本地解析失败的卡片 JSON 也会提交服务端校验/修复。
 * 未知组件名不视为卡片，避免模型输出的说明性 JSON 被误抽取（原样按文本展示）。
 */
export function looksLikeJsonCard(jsonContent) {
  if (!jsonContent || typeof jsonContent !== 'string') return false
  const trimmed = jsonContent.trim()
  if (!trimmed.startsWith('{')) return false
  // 只取开头一段，避免嵌套对象里的同名字段造成误判
  const head = trimmed.slice(0, 400)
  const componentMatch = head.match(/["']component["']\s*:\s*["']([a-zA-Z0-9_-]+)["']/i)
  if (componentMatch && SUPPORTED_COMPONENTS.includes(componentMatch[1].toLowerCase())) return true
  // type 字段的取值必须是受支持的组件名，避免普通 JSON 示例被当成卡片
  const typeMatch = head.match(/["']type["']\s*:\s*["']([a-zA-Z0-9_-]+)["']/i)
  return !!(typeMatch && SUPPORTED_COMPONENTS.includes(typeMatch[1].toLowerCase()))
}

/**
 * 从原始 markdown 文本中提取 JSON 代码块，替换为占位符。
 * 在 marked 处理之前调用，避免 marked 把 JSON 转成 <pre><code> 后正则失效。
 * 返回 { processedText, extractedBlocks }
 */
export function extractJsonCodeBlocks(text, includeInvalidCards = false) {
  if (!text) return { processedText: text || '', extractedBlocks: [] }

  const extractedBlocks = []
  let processedText = text
  let blockIndex = 0

  // 使用 indexOf 逐块提取 fenced code block
  // 兼容格式：
  //   标准: ```json\n{...}\n```
  //   紧凑: ```json{...}```
  let searchPos = 0
  const replacements = []

  while (searchPos < processedText.length) {
    const openIdx = processedText.indexOf('```', searchPos)
    if (openIdx === -1) break

    // 检查是否是 ```json（忽略大小写）
    const afterFence = processedText.slice(openIdx + 3).trimStart()
    if (!/^json\b/i.test(afterFence)) {
      searchPos = openIdx + 3
      continue
    }

    // 计算内容起始位置（跳过 ```json）
    const jsonLangLen = afterFence.match(/^json\b/i)[0].length
    const contentStart = openIdx + 3 + jsonLangLen

    // 查找关闭的 ```（必须是完整的 3 个反引号）
    const closeIdx = processedText.indexOf('```', contentStart)
    if (closeIdx === -1) {
      // 没有关闭的 ``` → 不完整的块，跳过（流式情况）
      searchPos = openIdx + 3
      continue
    }

    // 提取 ``` 之间的内容
    const jsonContent = processedText.slice(contentStart, closeIdx)
      .replace(/^[\s\n\r]+/, '') // 去掉开头的空白
      .replace(/[\s\n\r]+$/, '') // 去掉结尾的空白

    const payload = parseComponentPayload(jsonContent)
    if (payload) {
      const marker = `JSON_COMPONENT_${blockIndex}_PLACEHOLDER`
      replacements.push({
        start: openIdx,
        end: closeIdx + 3,
        marker,
        payload
      })
      extractedBlocks.push({ marker, payload, raw: jsonContent })
      blockIndex++
      searchPos = closeIdx + 3 // 从关闭的 ``` 之后继续搜索
    } else if (includeInvalidCards && looksLikeJsonCard(jsonContent)) {
      // 卡片形态但本地解析失败（非法 JSON / 结构不符 / 未知组件类型）→ 保留原文，交由校验修复器处理
      const marker = `JSON_COMPONENT_${blockIndex}_PLACEHOLDER`
      replacements.push({
        start: openIdx,
        end: closeIdx + 3,
        marker,
        payload: null
      })
      extractedBlocks.push({
        marker,
        payload: null,
        raw: jsonContent,
        error: diagnoseComponentJson(jsonContent)
      })
      blockIndex++
      searchPos = closeIdx + 3
    } else {
      searchPos = closeIdx + 3
    }
  }

  // 从后往前替换，避免索引偏移
  for (let i = replacements.length - 1; i >= 0; i--) {
    const r = replacements[i]
    processedText = processedText.slice(0, r.start) + r.marker + processedText.slice(r.end)
  }

  // 提取内联 JSON 组件（没有被 fenced block 包裹的 {"component":...}）
  const inlineMarkerRegex = /(?:"component"|'component'|\bcomponent\b)\s*:/gi
  let searchIndex = 0
  const inlineReplacements = []

  while (searchIndex < processedText.length) {
    inlineMarkerRegex.lastIndex = searchIndex
    const markerMatch = inlineMarkerRegex.exec(processedText)
    if (!markerMatch || markerMatch.index === undefined) break

    const markerIndex = markerMatch.index
    const markerLength = markerMatch[0].length

    // 跳过占位符中的 "component" 匹配
    if (markerIndex > 0 && /JSON_COMPONENT_\d+_PLACEHOLDER/.test(processedText.slice(Math.max(0, markerIndex - 30), markerIndex + markerLength))) {
      searchIndex = markerIndex + markerLength
      continue
    }

    const objectStart = processedText.lastIndexOf('{', markerIndex)
    // 起点必须紧邻 "component"，否则正文里更早出现的 { 会被误当作卡片起点，
    // 导致两个花括号之间的正常文字被整块吞掉
    if (objectStart === -1 || objectStart >= markerIndex || markerIndex - objectStart > 200) {
      searchIndex = markerIndex + markerLength
      continue
    }

    const objectEnd = findBalancedJsonObject(processedText, objectStart)
    if (objectEnd === -1) {
      // 括号不配对（缺右括号）且位于行首、形如卡片时：按空行边界只截取卡片本身，
      // 避免把其后的正文一并当作卡片原文吞进兜底卡片
      const beforeObject = processedText.slice(0, objectStart)
      // { 位于 ``` 代码块内部（含未闭合围栏）时不做截断推断，交回代码块逻辑，避免误提取
      const insideFence = (beforeObject.match(/```/g) || []).length % 2 === 1
      const atLineStart = objectStart === 0 || /\n\s*$/.test(beforeObject)
      const truncatedEnd = atLineStart && !insideFence ? findTruncatedJsonObjectEnd(processedText, objectStart) : -1
      const truncatedContent = truncatedEnd > objectStart ? processedText.slice(objectStart, truncatedEnd) : ''
      if (includeInvalidCards && truncatedContent && looksLikeJsonCard(truncatedContent)) {
        const marker = `JSON_COMPONENT_${blockIndex}_PLACEHOLDER`
        inlineReplacements.push({
          start: objectStart,
          end: truncatedEnd,
          marker,
          payload: null
        })
        extractedBlocks.push({
          marker,
          payload: null,
          raw: truncatedContent,
          error: diagnoseComponentJson(truncatedContent)
        })
        blockIndex++
        searchIndex = truncatedEnd
        continue
      }
      searchIndex = markerIndex + markerLength
      continue
    }

    const jsonContent = processedText.slice(objectStart, objectEnd + 1)
    const payload = parseComponentPayload(jsonContent)
    if (payload) {
      const marker = `JSON_COMPONENT_${blockIndex}_PLACEHOLDER`
      inlineReplacements.push({
        start: objectStart,
        end: objectEnd + 1,
        marker,
        payload
      })
      extractedBlocks.push({ marker, payload, raw: jsonContent })
      blockIndex++
      searchIndex = objectEnd + 1
    } else if (includeInvalidCards && looksLikeJsonCard(jsonContent)) {
      // 卡片形态但本地解析失败 → 保留原文，交由校验修复器处理
      const marker = `JSON_COMPONENT_${blockIndex}_PLACEHOLDER`
      inlineReplacements.push({
        start: objectStart,
        end: objectEnd + 1,
        marker,
        payload: null
      })
      extractedBlocks.push({
        marker,
        payload: null,
        raw: jsonContent,
        error: diagnoseComponentJson(jsonContent)
      })
      blockIndex++
      searchIndex = objectEnd + 1
    } else {
      searchIndex = markerIndex + markerLength
    }
  }

  // 从后往前替换内联 JSON
  for (let i = inlineReplacements.length - 1; i >= 0; i--) {
    const r = inlineReplacements[i]
    processedText = processedText.slice(0, r.start) + r.marker + processedText.slice(r.end)
  }

  return { processedText, extractedBlocks }
}

/**
 * 重新解析 JSON 卡片文本，提取组件 payload
 * 供 json-card/check 返回的 cardJson 使用（可能已被修复）
 * @param {string} jsonText 完整 JSON 卡片文本
 * @returns {{ tagName, props } | null} 解析失败返回 null
 */
export function reparseComponentJson(jsonText) {
  if (!jsonText || typeof jsonText !== 'string') return null
  // 优先把整段文本当作组件 JSON 直接解析：
  // 兼容仅含 type 字段（无 component 字段）、且不带 ```json 围栏的卡片文本，
  // 这类文本无法被 extractJsonCodeBlocks 的内联扫描识别
  const direct = parseComponentPayload(jsonText)
  if (direct) return direct
  const { extractedBlocks } = extractJsonCodeBlocks(jsonText)
  const block = extractedBlocks && extractedBlocks[0]
  return block && block.payload ? block.payload : null
}

/**
 * Parse markdown text and extract custom component blocks.
 * Returns an array of segments: { type: 'text', content } or { type: 'component', tagName, props }
 * When isStreaming is true, incomplete JSON blocks show as 'component-loading' placeholders.
 */
export function parseMarkdownComponents(text, isStreaming) {
  if (!text) return [{ type: 'text', content: '' }]

  const segments = []

  // Check both inline JSON and fenced code blocks
  const inlineMatches = findInlineJsonComponentMatches(text)
  const fencedMatches = findFencedCodeBlockMatches(text)

  // Merge and deduplicate matches
  const allMatches = [...inlineMatches, ...fencedMatches]
    .sort((a, b) => a.start - b.start)
    .filter((m, i, arr) => i === 0 || m.start >= arr[i - 1].end)

  let lastIndex = 0
  for (const m of allMatches) {
    if (m.start > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, m.start) })
    }
    segments.push({
      type: 'component',
      tagName: m.payload.tagName,
      props: m.payload.props
    })
    lastIndex = m.end
  }

  // 处理末尾剩余文本
  let tail = lastIndex < text.length ? text.slice(lastIndex) : ''

  // 流式输出时：检测未闭合的代码块
  if (isStreaming && tail) {
    const backtickCount = (tail.match(/```/g) || []).length
    const isUnclosed = backtickCount % 2 === 1

    if (isUnclosed) {
      const lastIdx = tail.lastIndexOf('```')
      const beforeFenced = tail.slice(0, lastIdx)
      const afterFenced = tail.slice(lastIdx)

      // 未闭合的 JSON 组件代码块 → 显示 loading 占位
      const incompleteFenced = afterFenced.match(/```(?:json|chart|card)\s*\n([\s\S]*)$/i)
      if (incompleteFenced) {
        if (beforeFenced.trim()) {
          segments.push({ type: 'text', content: beforeFenced })
        }
        const partial = incompleteFenced[1] || ''
        const componentMatch = partial.match(/"component"\s*:\s*"([^"]+)"/)
        const typeMatch = !componentMatch ? partial.match(/"type"\s*:\s*"([^"]+)"/) : null
        const matchedType = (componentMatch && componentMatch[1]) || (typeMatch && typeMatch[1]) || ''
        if (matchedType && SUPPORTED_COMPONENTS.includes(matchedType.toLowerCase())) {
          const compType = matchedType.toLowerCase()
          const titleMatch = partial.match(/"title"\s*:\s*"([^"]*)"/)
          const typeName = COMPONENT_TYPE_NAMES[compType] || compType
          const loadingText = titleMatch && titleMatch[1]
            ? t('workbench.loadingComponent', { name: titleMatch[1], type: typeName })
            : t('workbench.componentLoading', { type: typeName })
          segments.push({ type: 'component-loading', loadingText })
        }
        tail = ''
      } else {
        // 普通未闭合代码块 → 只保留前面的文本
        if (beforeFenced.trim()) {
          segments.push({ type: 'text', content: beforeFenced })
        }
        tail = ''
      }
    }
  }

  // 流式输出时：检测未闭合的 <pre><code> HTML 块
  if (isStreaming && tail) {
    const incompleteHtmlCode = tail.match(/<pre><code(?:\s+class="language-json")?>([\s\S]*)$/i)
    if (incompleteHtmlCode) {
      const beforeCode = tail.slice(0, tail.length - incompleteHtmlCode[0].length)
      if (beforeCode.trim()) {
        segments.push({ type: 'text', content: beforeCode })
      }
      const partial = decodeHtmlEntities(incompleteHtmlCode[1] || '')
      const componentMatch = partial.match(/"component"\s*:\s*"([^"]+)"/)
      const typeMatch = !componentMatch ? partial.match(/"type"\s*:\s*"([^"]+)"/) : null
      const matchedType = (componentMatch && componentMatch[1]) || (typeMatch && typeMatch[1]) || ''
      if (matchedType && SUPPORTED_COMPONENTS.includes(matchedType.toLowerCase())) {
        const compType = matchedType.toLowerCase()
        const titleMatch = partial.match(/"title"\s*:\s*"([^"]*)"/)
        const typeName = COMPONENT_TYPE_NAMES[compType] || compType
        const loadingText = titleMatch && titleMatch[1]
          ? t('workbench.loadingComponent', { name: titleMatch[1], type: typeName })
          : t('workbench.componentLoading', { type: typeName })
        segments.push({ type: 'component-loading', loadingText })
      }
      tail = ''
    }
  }

  // 流式输出时：检测截断的内联 JSON {"component":... 或 {"type":...
  //    只在不在 ``` 代码块内部时触发（避免吞掉整个代码块及后面内容）
  if (isStreaming && tail) {
    const inlineTruncated = tail.match(/\{\s*["'](?:component|type)["']\s*:\s*/)
    if (inlineTruncated) {
      const beforeJson = tail.slice(0, inlineTruncated.index)
      // 检查 { 前面是否有未闭合的 ```（即 { 在 ``` 代码块内部）
      const backticksBefore = (beforeJson.match(/```/g) || []).length
      const insideCodeBlock = backticksBefore % 2 === 1
      if (!insideCodeBlock) {
        if (beforeJson.trim()) {
          segments.push({ type: 'text', content: beforeJson })
        }
        const partial = tail.slice(inlineTruncated.index)
        const compMatch = partial.match(/"component"\s*:\s*"([^"]+)"/)
        const typeMatch = !compMatch ? partial.match(/"type"\s*:\s*"([^"]+)"/) : null
        const matchedType = (compMatch && compMatch[1]) || (typeMatch && typeMatch[1]) || ''
        if (matchedType && SUPPORTED_COMPONENTS.includes(matchedType.toLowerCase())) {
          const compType = matchedType.toLowerCase()
          const titleMatch = partial.match(/"title"\s*:\s*"([^"]*)"/)
          const typeName = COMPONENT_TYPE_NAMES[compType] || compType
          const loadingText = titleMatch && titleMatch[1]
            ? t('workbench.loadingComponent', { name: titleMatch[1], type: typeName })
            : t('workbench.componentLoading', { type: typeName })
          segments.push({ type: 'component-loading', loadingText })
        }
        tail = ''
      }
    }
  }

  if (tail) {
    segments.push({ type: 'text', content: tail })
  }

  return segments.filter(s => {
    if (s.type === 'text') return s.content && s.content.trim().length > 0
    return true
  })
}

export function generateComponentKey(tagName, props) {
  const title = props.title || ''
  const type = props.type || ''
  const dataLength = props.data?.length || 0
  const hash = `${tagName}-${title}-${type}-${dataLength}`
  let hashSum = 0
  for (let i = 0; i < hash.length; i++) {
    hashSum = (hashSum << 5) - hashSum + hash.charCodeAt(i)
    hashSum = hashSum & hashSum
  }
  return `component-${tagName}-${Math.abs(hashSum)}`
}
